import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus, Trash, FloppyDisk, CircleNotch, IdentificationBadge,
  Wrench, Motorcycle, PaintBrush, User, Storefront,
} from "@phosphor-icons/react";

const SPECIALTIES = [
  { value: "taller", labelEs: "Taller (estatus/servicio)", labelEn: "Workshop (status/service)", icon: Wrench },
  { value: "motocicletas", labelEs: "Motocicletas", labelEn: "Motorcycles", icon: Motorcycle },
  { value: "customer_care", labelEs: "Customer Care", labelEn: "Customer Care", icon: User },
  { value: "carroceria_pintura", labelEs: "Carrocería y Pintura", labelEn: "Body & Paint", icon: PaintBrush },
  { value: "general", labelEs: "General", labelEn: "General", icon: Storefront },
];
const BRANCHES = ["Kennedy", "Luperón", "Santiago", "Punta Cana", "RapidCentro", "Auto Pintura Robles"];

export default function ServiceAdvisorsEditor({ lang }) {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    name: "", phone: "", email: "", branch: "Kennedy",
    specialty: "taller", notes: "",
  });

  useEffect(() => {
    let alive = true;
    api.get("/service-advisors")
      .then((r) => { if (alive) { setAdvisors(r.data || []); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  async function save() {
    if (!draft.name.trim() || !draft.phone.trim()) {
      toast.error(lang === "es" ? "Nombre y teléfono son requeridos" : "Name and phone required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/service-advisors", draft);
      setAdvisors((cs) => [...cs, data]);
      setDraft({ name: "", phone: "", email: "", branch: "Kennedy", specialty: "taller", notes: "" });
      toast.success(lang === "es" ? "Asesor agregado" : "Advisor added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function updateAdvisor(id, patch) {
    try {
      const { data } = await api.patch(`/service-advisors/${id}`, patch);
      setAdvisors((cs) => cs.map((a) => (a.id === id ? data : a)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  }

  async function remove(id) {
    if (!window.confirm(lang === "es" ? "¿Eliminar este asesor?" : "Delete this advisor?")) return;
    try {
      await api.delete(`/service-advisors/${id}`);
      setAdvisors((cs) => cs.filter((a) => a.id !== id));
      toast.success(lang === "es" ? "Eliminado" : "Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  }

  if (loading) {
    return <div className="text-xs text-muted-foreground flex items-center gap-2"><CircleNotch size={12} className="animate-spin" />Cargando…</div>;
  }

  return (
    <div data-testid="service-advisors-editor">
      <div className="text-sm font-medium mb-1">
        {lang === "es" ? "Asesores de Servicio" : "Service Advisors"}
      </div>
      <div className="text-xs text-muted-foreground mb-4">
        {lang === "es"
          ? "Contactos reales que el bot comparte cuando el cliente pregunta por estatus de vehículo en taller o motocicletas Yamaha."
          : "Real contacts the bot shares when a customer asks about vehicle-in-workshop status or Yamaha motorcycles."}
      </div>

      {/* Add form */}
      <div className="rounded-md border border-primary/30 bg-primary/5 p-3 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
          {lang === "es" ? "Nuevo asesor" : "New advisor"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <Input
            placeholder={lang === "es" ? "Nombre" : "Name"}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="advisor-new-name"
            className="h-8 md:col-span-2"
          />
          <Input
            placeholder={lang === "es" ? "Teléfono" : "Phone"}
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            data-testid="advisor-new-phone"
            className="h-8"
          />
          <Input
            placeholder="Email (opcional)"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            data-testid="advisor-new-email"
            className="h-8"
          />
          <select
            value={draft.specialty}
            onChange={(e) => setDraft({ ...draft, specialty: e.target.value })}
            data-testid="advisor-new-specialty"
            className="h-8 rounded-md border border-input bg-background text-xs px-2"
          >
            {SPECIALTIES.map((s) => <option key={s.value} value={s.value}>{lang === "es" ? s.labelEs : s.labelEn}</option>)}
          </select>
          <select
            value={draft.branch}
            onChange={(e) => setDraft({ ...draft, branch: e.target.value })}
            data-testid="advisor-new-branch"
            className="h-8 rounded-md border border-input bg-background text-xs px-2"
          >
            {BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="mt-2 flex justify-between items-center gap-2">
          <Textarea
            placeholder={lang === "es" ? "Notas internas (opcional)" : "Internal notes (optional)"}
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            data-testid="advisor-new-notes"
            rows={1}
            className="flex-1 text-xs"
          />
          <Button size="sm" onClick={save} disabled={saving} data-testid="advisor-save">
            {saving ? <CircleNotch size={12} className="animate-spin mr-1"/> : <Plus size={12} className="mr-1"/>}
            {lang === "es" ? "Agregar" : "Add"}
          </Button>
        </div>
      </div>

      {/* List */}
      {advisors.length === 0 ? (
        <div className="text-center text-xs text-muted-foreground py-6 border border-dashed border-border rounded-md">
          {lang === "es" ? "Aún no hay asesores registrados." : "No advisors yet."}
        </div>
      ) : (
        <div className="rounded-md border border-border overflow-hidden" data-testid="advisors-table">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{lang === "es" ? "Nombre" : "Name"}</th>
                <th className="px-3 py-2 font-medium">{lang === "es" ? "Teléfono" : "Phone"}</th>
                <th className="px-3 py-2 font-medium">{lang === "es" ? "Especialidad" : "Specialty"}</th>
                <th className="px-3 py-2 font-medium">{lang === "es" ? "Sucursal" : "Branch"}</th>
                <th className="px-3 py-2 font-medium text-center">{lang === "es" ? "Activo" : "Active"}</th>
                <th className="px-3 py-2 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {advisors.map((a) => (
                <tr key={a.id} className="border-t border-border" data-testid={`advisor-row-${a.id}`}>
                  <td className="px-3 py-2 font-medium">
                    {a.name}
                    {a.email && <div className="text-[10px] text-muted-foreground">{a.email}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{a.phone}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[9px] capitalize">
                      {a.specialty.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{a.branch}</td>
                  <td className="px-3 py-2 text-center">
                    <Switch
                      checked={a.active}
                      onCheckedChange={(v) => updateAdvisor(a.id, { active: v })}
                      data-testid={`advisor-active-${a.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => remove(a.id)}
                      className="text-destructive hover:bg-destructive/10 p-1 rounded"
                      data-testid={`advisor-delete-${a.id}`}
                    ><Trash size={12}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
